"use client";

import { c as _c } from "react/compiler-runtime";
import { Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { isOffPeak as isOff, nextChange as next, type OffPeakSchedule } from "@/core/off-peak";
import { cx } from "@/ds";

/** A schedule as the public catalogue sends it: hours only, no prices or costs. */
export type Schedule = Pick<OffPeakSchedule, "timeZone" | "peak"> & {
  offPeakDates?: string[];
};
const full = (s: Schedule): OffPeakSchedule => ({
  ...s,
  prices: {
    input: "0",
    output: "0"
  }
});
export const isOffPeak = (s: Schedule, at: Date) => isOff(full(s), at);
const nextChange = (s: Schedule, at: Date) => next(full(s), at);

/** "2 h 10 min", "45 min". */
export function until(from: Date, to: Date) {
  const minutes = Math.max(1, Math.round((to.getTime() - from.getTime()) / 60_000));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h} h${m ? ` ${m} min` : ""}` : `${m} min`;
}

/** The next off-peak periods after `from`, as [start, end] pairs. */
export function upcoming(schedule: Schedule, from: Date, count = 3) {
  const periods: [Date, Date | null][] = [];
  let cursor = from;
  if (isOffPeak(schedule, from)) {
    const end = nextChange(schedule, from);
    periods.push([from, end]);
    if (!end) {
      return periods;
    }
    cursor = end;
  }
  while (periods.length < count) {
    const start = nextChange(schedule, cursor);
    if (!start) {
      break;
    }
    const end = nextChange(schedule, start);
    periods.push([start, end]);
    if (!end) {
      break;
    }
    cursor = end;
  }
  return periods;
}
const time = (date: Date) => date.toLocaleString(undefined, {
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit"
});

/**
 * Off-peak status for a model, live: whether it's off-peak now and when
 * that changes, and the next off-peak periods in the visitor's own time.
 * Prices are only shown off-peak once the gateway bills them that way.
 */
export function OffPeakNotice(t0) {
  const $ = _c(47);
  const {
    schedule,
    discount,
    compact
  } = t0;
  const [now, setNow] = useState(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setNow(new Date());
      const timer = setInterval(() => setNow(new Date()), 60000);
      return () => clearInterval(timer);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  if (!now) {
    return null;
  }
  let t3;
  if ($[2] !== now || $[3] !== schedule) {
    t3 = isOffPeak(schedule, now);
    $[2] = now;
    $[3] = schedule;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const off = t3;
  let t4;
  if ($[5] !== discount || $[6] !== now || $[7] !== off || $[8] !== schedule) {
    const change = nextChange(schedule, now);
    let t5;
    if ($[10] !== discount) {
      t5 = discount && discount > 0 ? `${Math.round(discount * 100)}% off` : "lower prices";
      $[10] = discount;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    const saving = t5;
    t4 = off ? `Off-peak now: ${saving}${change ? `, ends in ${until(now, change)}` : ""}` : `Peak hours now${change ? `: off-peak (${saving}) starts in ${until(now, change)}` : ""}`;
    $[5] = discount;
    $[6] = now;
    $[7] = off;
    $[8] = schedule;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const status = t4;
  if (compact) {
    const t5 = off ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-amber-500/10 text-amber-700 dark:text-amber-400";
    let t6;
    if ($[12] !== t5) {
      t6 = cx("inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium", t5);
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== off) {
      t7 = off ? <Moon className="size-3" /> : <Sun className="size-3" />;
      $[14] = off;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== status || $[17] !== t6 || $[18] !== t7) {
      t8 = <span className={t6}>{t7}{status}</span>;
      $[16] = status;
      $[17] = t6;
      $[18] = t7;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    return t8;
  }
  const weekendsOff = !schedule.peak.some(_temp);
  const t5 = off ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5";
  let t6;
  if ($[20] !== t5) {
    t6 = cx("rounded-lg border p-4 text-sm", t5);
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== off) {
    t7 = off ? <Moon className="size-4 text-emerald-600" /> : <Sun className="size-4 text-amber-600" />;
    $[22] = off;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== status || $[25] !== t7) {
    t8 = <p className="flex items-center gap-2 font-medium">{t7}{status}</p>;
    $[24] = status;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="mt-2 text-xs text-muted-foreground">Next off-peak hours, in your time:</p>;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== now || $[29] !== schedule) {
    let t11;
    if ($[31] !== now) {
      t11 = t12 => {
        const [start, end] = t12;
        return <li key={start.toISOString()}>{start.getTime() === now.getTime() ? "Now" : time(start)} to{" "}{end ? time(end) : "later"}</li>;
      };
      $[31] = now;
      $[32] = t11;
    } else {
      t11 = $[32];
    }
    t10 = upcoming(schedule, now).map(t11);
    $[28] = now;
    $[29] = schedule;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[33] !== t10) {
    t11 = <ul className="mt-1 space-y-0.5 text-xs tabular-nums">{t10}</ul>;
    $[33] = t10;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== schedule.peak) {
    t12 = schedule.peak.map(_temp2).join(" and ");
    $[35] = schedule.peak;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const t13 = schedule.timeZone === "UTC" ? "UTC" : `${schedule.timeZone} time`;
  const t14 = weekendsOff ? " on weekdays; weekends are off-peak" : "";
  const t15 = schedule.offPeakDates?.length ? ", and listed public holidays are off-peak" : "";
  let t16;
  if ($[37] !== t12 || $[38] !== t13 || $[39] !== t14 || $[40] !== t15) {
    t16 = <p className="mt-2 text-xs text-muted-foreground">Busy hours are{" "}{t12}{" "}{t13}{t14}{t15}. A request is billed at the prices of the minute it starts.</p>;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t11 || $[43] !== t16 || $[44] !== t6 || $[45] !== t8) {
    t17 = <div className={t6}>{t8}{t9}{t11}{t16}</div>;
    $[42] = t11;
    $[43] = t16;
    $[44] = t6;
    $[45] = t8;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  return t17;
}
function _temp2(w_0) {
  return `${w_0.start}–${w_0.end}`;
}
function _temp(w) {
  return w.days.includes(0) || w.days.includes(6);
}